/**
 * Contact & Support Page
 * ThePedigreeApp
 */

import { useState } from 'react';
import { 
  Mail, Phone, MapPin, Clock, Send, MessageSquare, 
  HelpCircle, FileText, CheckCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, Button, Input } from '../components/shared/UIComponents';
import { cn } from '../utils/helpers';

const FAQs = [
  {
    question: "How do I register as a breeder?",
    answer: "Click 'Register' and select 'I'm a Breeder'. You'll need to provide your cattery name, registry body (SACC, CASA, or CFSA), and registry number. We verify all breeders before approval, which usually takes 1-2 business days."
  },
  {
    question: "How much does it cost to list kittens?",
    answer: "Listing is free! We only charge a 10% commission when your kitten sells. There are no monthly fees or upfront costs."
  },
  {
    question: "How do I get verified as a breeder?",
    answer: "After registering, our team verifies your registry credentials with SACC, CASA, or CFSA. Once confirmed, you'll receive an email and can start listing your cats and kittens."
  },
  {
    question: "Is my information safe?",
    answer: "Yes. We use industry-standard encryption and never share your personal details with third parties without your consent. See our Privacy Policy for full details."
  },
  {
    question: "How do payments work?",
    answer: "Payments are handled directly between buyer and breeder. We don't process payments - we simply connect verified breeders with serious buyers. Our commission is invoiced monthly."
  },
  {
    question: "Can I list cats from other countries?",
    answer: "Currently, ThePedigreeApp is only available for South African breeders registered with local registry bodies. We may expand internationally in the future."
  },
  {
    question: "How do I report a problem with a breeder or buyer?",
    answer: "Use the contact form below or email us at support@thepedigreeapp.co.za. We take all complaints seriously and will investigate promptly."
  },
  {
    question: "Can I use the app on my phone?",
    answer: "Yes! ThePedigreeApp is fully responsive and works on any device - phone, tablet, or computer."
  }
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitted(true);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-feline-600 to-feline-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-display font-bold mb-4">Contact & Support</h1>
          <p className="text-xl text-feline-100 max-w-2xl mx-auto">
            We're here to help. Reach out with questions, feedback, or support requests.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <div className="space-y-6">
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Get in Touch</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-feline-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-feline-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <a href="mailto:support@thepedigreeapp.co.za" className="text-feline-600 hover:underline">
                      support@thepedigreeapp.co.za
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-feline-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-feline-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Phone</p>
                    <a href="tel:+27000000000" className="text-feline-600 hover:underline">
                      +27 (0) 00 000 0000
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-feline-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-feline-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Location</p>
                    <p className="text-gray-600">Western Cape, South Africa</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-feline-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-feline-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Support Hours</p>
                    <p className="text-gray-600">Mon - Fri: 9am - 5pm SAST</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Links</h2>
              <div className="space-y-2">
                <a href="/privacy" className="flex items-center gap-2 text-gray-600 hover:text-feline-600 transition-colors">
                  <FileText className="w-4 h-4" /> Privacy Policy
                </a>
                <a href="/terms" className="flex items-center gap-2 text-gray-600 hover:text-feline-600 transition-colors">
                  <FileText className="w-4 h-4" /> Terms of Service
                </a>
                <a href="/community" className="flex items-center gap-2 text-gray-600 hover:text-feline-600 transition-colors">
                  <MessageSquare className="w-4 h-4" /> Community
                </a>
              </div>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Send us a Message</h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-paw-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-paw-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600 mb-6">We'll get back to you within 24-48 hours.</p>
                  <Button onClick={() => setSubmitted(false)} variant="outline">Send Another Message</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Your Name *" name="name" value={formData.name} onChange={handleChange} placeholder="John Smith" required />
                    <Input label="Email Address *" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Category *</label>
                      <select name="category" value={formData.category} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-feline-500 focus:border-feline-500" required>
                        <option value="">Select a category</option>
                        <option value="general">General Inquiry</option>
                        <option value="support">Technical Support</option>
                        <option value="billing">Billing Question</option>
                        <option value="verification">Breeder Verification</option>
                        <option value="report">Report a Problem</option>
                        <option value="partnership">Partnership Inquiry</option>
                        <option value="feedback">Feedback/Suggestion</option>
                      </select>
                    </div>
                    <Input label="Subject *" name="subject" value={formData.subject} onChange={handleChange} placeholder="What's this about?" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={6} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-feline-500 focus:border-feline-500 resize-none" placeholder="Tell us how we can help..." required />
                  </div>
                  <Button type="submit" className="w-full" loading={loading}>
                    <Send className="w-4 h-4" /> Send Message
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-display font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {FAQs.map((faq, index) => (
              <Card key={index} className={cn("cursor-pointer transition-all", openFAQ === index ? "ring-2 ring-feline-500" : "")} onClick={() => setOpenFAQ(openFAQ === index ? null : index)}>
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 pr-4">{faq.question}</h3>
                  {openFAQ === index ? <ChevronUp className="w-5 h-5 text-feline-500 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                </div>
                {openFAQ === index && <p className="mt-4 text-gray-600 border-t pt-4">{faq.answer}</p>}
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
